import React, { useState, useEffect, useRef } from 'react';

/**
 * AnimatedSection is a reusable wrapper that animates its children
 * when they scroll into view using IntersectionObserver.
 */
export default function AnimatedSection({ 
  children, 
  className = '', 
  delay = 0, 
  duration = 800, 
  direction = 'up' 
}) {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.05, // Trigger early for better responsiveness
        rootMargin: '0px 0px -30px 0px',
      }
    );

    const currentRef = elementRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const getDirectionClass = () => {
    switch (direction) {
      case 'up': return 'translate-y-8';
      case 'down': return '-translate-y-8';
      case 'left': return 'translate-x-8';
      case 'right': return '-translate-x-8';
      case 'scale': return 'scale-95';
      case 'none': return '';
      default: return 'translate-y-8';
    }
  };

  return (
    <div
      ref={elementRef}
      className={`transition-all ease-out ${className} ${
        isVisible 
          ? 'opacity-100 translate-y-0 translate-x-0 scale-100' 
          : `opacity-0 ${getDirectionClass()}`
      }`}
      style={{ 
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms` 
      }}
    >
      {children}
    </div>
  );
}
